import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  const processAuth = useCallback(async (hash) => {
    try {
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('No session ID found. Please try signing in again.');
        setTimeout(() => navigate('/', { replace: true }), 3000);
        return;
      }
      
      // Exchange session_id for session_token
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        throw new Error(`Failed to exchange session: ${response.status}`);
      }

      const userData = await response.json();
      
      // Store user in localStorage as backup
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUserData(userData);

      // Small delay to ensure cookie is set before redirect
      await new Promise(resolve => setTimeout(resolve, 300));

      // Navigate to member dashboard
      window.location.href = '/dashboard';

    } catch (err) {
      // Log error for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Auth callback error:', err.message);
      }
      setError('Sign in failed. Please try again.');
      setTimeout(() => navigate('/', { replace: true }), 3000);
    }
  }, [navigate, setUserData]);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    processAuth(location.hash);
  }, [location.hash, processAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-grey">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-brand-slate mb-2">{error}</p>
          <p className="text-slate-400 text-sm">Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-grey">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-brand-slate">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
